import * as React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { UseFormReturn } from 'react-hook-form';
import { pickBy } from 'lodash';

import { useMutation } from '@tanstack/react-query';
import {
  EmployerSignupAcceptance,
  buildEmployerSignupAcceptance,
  generateSignupIdempotencyKey,
  useAccountsService,
  useRestaurantsService,
} from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { activeAddressIdAtom, activeRestaurantAtom, googleAddresInputValueAtom } from './google-address-input-result';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import { restaurantInformationDataAtom, useRestaurantOnboardingData } from './use-restaurant-onboarding-data-query';
import { binaryPicturesAtom } from './restaurant-onboarding-image-gallery';
import {
  activeRestaurantOnboardingStateAtom,
  isManualEntryAtom,
  manualAddressFieldsAtom,
  restaurantOnboardingFormDefaultValues,
  restaurantOnboardingModalVisibleAtom,
} from '.';
import { useV2Router } from '../../dashboard/hooks/use-v2-router';

// hooks
export const useRestaurantOnboardingResetActions = () => {
  const setGoogleInputAddressValue = useSetAtom(googleAddresInputValueAtom);
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const restaurantOnboardingModalVisibleToggler = useSetAtom(restaurantOnboardingModalVisibleAtom);
  const setBinaryPictures = useSetAtom(binaryPicturesAtom);
  const setActiveRestaurantName = useSetAtom(activeRestaurantAtom);
  const setActiveGoogleAddressId = useSetAtom(activeAddressIdAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  const setManualAddressFields = useSetAtom(manualAddressFieldsAtom);

  const resetState = React.useCallback(() => {
    const timeoutId = setTimeout(() => {
      setActiveRestaurantName('');
      setActiveGoogleAddressId('');
      setGoogleInputAddressValue('');
      setActiveUI('select_restaurant_from_google');
      setBinaryPictures([]);
      setRestaurantInformationData({});
      setIsManualEntry(false);
      setManualAddressFields({});
    }, 250); // for delay before closing the modal
    return timeoutId;
  }, [
    setActiveGoogleAddressId,
    setActiveRestaurantName,
    setActiveUI,
    setBinaryPictures,
    setGoogleInputAddressValue,
    setIsManualEntry,
    setManualAddressFields,
    setRestaurantInformationData,
  ]);

  const handleModalClose = React.useCallback(() => {
    restaurantOnboardingModalVisibleToggler(false);
    resetState();
  }, [resetState, restaurantOnboardingModalVisibleToggler]);

  return { resetState, handleModalClose };
};

export const useFormatRestaurantOnboardingData = () => {
  const businessAddress = useAtomValue(activeRestaurantAtom);
  const binaryPictures = useAtomValue(binaryPicturesAtom);
  const isGettingStartedPage = useGettingStartedPage();
  const isManualEntry = useAtomValue(isManualEntryAtom);
  const manualAddressFields = useAtomValue(manualAddressFieldsAtom);
  // One key per form attempt: every format of the same form (and a retry of its submit) reuses it.
  const idempotencyKeyRef = React.useRef<string | null>(null);

  const formatFormData = React.useCallback(
    (data: RestaurantOnboardingPayload) => {
      // The boxes never travel as contact fields: on the getting-started page they become the
      // `acceptance` object of `POST /organizations` (RW-422), sent only when both required boxes are ticked.
      const {
        terms_and_conditions,
        tracking_consent,
        vexatious_clauses_acceptance,
        number_of_staffs,
        pictures,
        ...payloadData
      } = data;
      let payload = {};
      let acceptance: EmployerSignupAcceptance | undefined;
      if (isGettingStartedPage && terms_and_conditions && vexatious_clauses_acceptance) {
        if (!idempotencyKeyRef.current) idempotencyKeyRef.current = generateSignupIdempotencyKey();
        acceptance = buildEmployerSignupAcceptance(tracking_consent, idempotencyKeyRef.current);
      }

      const manualEntryGeoData = isManualEntry
        ? {
            business_address: `${data.restaurant_name}, ${data.address}`,
            ...(manualAddressFields.address_lat !== null && manualAddressFields.address_lat !== undefined
              ? { address_lat: manualAddressFields.address_lat }
              : {}),
            ...(manualAddressFields.address_lon !== null && manualAddressFields.address_lon !== undefined
              ? { address_lon: manualAddressFields.address_lon }
              : {}),
            // `locality` intentionally NOT taken from manualAddressFields here:
            // it now comes from the required, editable City form field (data.locality
            // via payloadData), which is auto-filled on suggestion-select but can be
            // typed manually. This is what fixes manual-entry restaurants saving with
            // a NULL locality. Country/postal_code/admin areas stay best-effort from
            // the geocoded selection.
            ...(manualAddressFields.country ? { country: manualAddressFields.country } : {}),
            ...(manualAddressFields.postal_code ? { postal_code: manualAddressFields.postal_code } : {}),
            ...(manualAddressFields.administrative_area_level_1
              ? { administrative_area_level_1: manualAddressFields.administrative_area_level_1 }
              : {}),
            ...(manualAddressFields.administrative_area_level_2
              ? { administrative_area_level_2: manualAddressFields.administrative_area_level_2 }
              : {}),
          }
        : {};

      const additionalRestaurantData = {
        ...(pictures?.length ? { pictures } : {}),
        ...(number_of_staffs
          ? {
              staff_size_floor: Math.round(+number_of_staffs / 2),
              staff_size_kitchen: Math.round(+number_of_staffs / 2),
            }
          : {}),
        ...(binaryPictures?.length ? { binary_pictures: binaryPictures.map((file) => file.file) } : {}),
        ...(!isManualEntry ? { business_address: businessAddress } : {}),
        show_details: true,
        ...manualEntryGeoData,
      };
      const orgContactInfoArray = ['name', 'surname', 'email', 'phone_number'];
      if (isGettingStartedPage) {
        const contactInformation = pickBy(payloadData, (_, key) => orgContactInfoArray.includes(key));
        const restaurantInfo = pickBy(payloadData, (_, key) => !orgContactInfoArray.includes(key));
        payload = {
          employer_user_contacts: {
            ...contactInformation,
          },
          restaurants: {
            ...restaurantInfo,
            ...additionalRestaurantData,
          },
          ...(acceptance ? { acceptance } : {}),
        };
      } else {
        payload = {
          ...payloadData,
          ...additionalRestaurantData,
        };
      }
      return payload;
    },
    [binaryPictures, businessAddress, isGettingStartedPage, isManualEntry, manualAddressFields]
  );

  return formatFormData;
};
// Custom Hook for Form reset Logic
export const useRestaurantOnboardingFormReset = ({
  methods,
  skipInitialReset = false,
}: {
  methods?: UseFormReturn<RestaurantOnboardingPayload, any>;
  skipInitialReset?: boolean;
}) => {
  const { user } = useAuth();
  const restaurantOnboardingData = useRestaurantOnboardingData();
  const restaurantOnboardingModalVisible = useAtomValue(restaurantOnboardingModalVisibleAtom);
  const { resetState } = useRestaurantOnboardingResetActions();

  React.useEffect(() => {
    if (!skipInitialReset) {
      const timeoutId = resetState();
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [resetState, restaurantOnboardingModalVisible, skipInitialReset]);

  React.useEffect(() => {
    methods.reset({
      ...restaurantOnboardingFormDefaultValues,
    });
  }, [methods]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (restaurantOnboardingData)
        methods.reset({
          ...restaurantOnboardingData,
          ...(user?.email
            ? {
                email: user?.email || '',
                firstName: user?.name || '',
                lastName: user?.surname || '',
                phone_number: user?.phone_number || '',
              }
            : {}),
          number_of_staffs: (
            (restaurantOnboardingData.staff_size_floor || 0) + (restaurantOnboardingData.staff_size_kitchen || 0)
          ).toString(),
          email: user?.email || '',
        });
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [restaurantOnboardingData, methods, user?.email, user?.name, user?.surname, user?.phone_number]);
};

export const useGettingStartedPage = () => {
  const { pathname } = useV2Router();
  return pathname.includes('/getting-started');
};

export const useAccountAlreadyExistOrNotMutation = (fireAnalyticsEvent = true) => {
  const setActiveState = useSetAtom(activeRestaurantOnboardingStateAtom);
  const accountServices = useAccountsService();
  const { trackPHEvent } = useAnalytics();

  const queryData = useMutation({
    mutationFn: async (params: { email?: string; phone_number?: string }) =>
      (await accountServices.checkUserExistence(params)).data,
    // retry: 1,
    onSuccess() {
      setActiveState('otp_verification');
      if (fireAnalyticsEvent) {
        trackPHEvent('freemium_employer_confirms_user_data', {});
      }
    },
    onError(error) {
      if (error?.['code'] === 409) {
        // Any conflict → show "contact already exists" (login redirect)
        setActiveState('contact_already_exists');
      }
    },
  });
  return queryData;
};

export const useCheckRestaurantDuplicate = () => {
  const restaurantServices = useRestaurantsService();
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);
  const manualAddressFields = useAtomValue(manualAddressFieldsAtom);

  return useMutation({
    mutationFn: async (params: { restaurant_name: string }) => {
      const { address_lat, address_lon } = manualAddressFields;
      if (!address_lat || !address_lon) return { duplicate: false };
      return (
        await restaurantServices.checkRestaurantDuplicate({
          restaurant_name: params.restaurant_name,
          address_lat,
          address_lon,
        })
      ).data;
    },
    onError(error) {
      if (error?.['code'] === 409) {
        const data = error?.['data'] || {};
        setRestaurantInformationData({
          restaurant_name: data.restaurant_name,
          locality: data.locality,
          organization_id: data.organization_id,
        });
        setActiveUI('restaurant_already_exists');
      }
    },
  });
};
